import { useState, useMemo } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { shortMonthDayAndShortYear, shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import type { IDropdownItem, IDuration } from "@similarweb/ui-components";
import {
    DurationSelectorStateless,
    EGranularity,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useDurationContext } from "../../../contexts/DurationContext";
import { formatDuration } from "../../../utils/formatDuration";

import { DurationSelectorButton } from "./DurationSelectorButton";
import { useMinMaxDate } from "./useMinMaxDate";
import { SAME_DAY_PRESET_ID, usePresets } from "./usePresets";
import { useSelectedPreset } from "./useSelectedPreset";

const StyledPopoverContent = styled(PopoverContent)`
    padding: 0;
`;

const noop = () => {};

const getUserLanguage = (): string => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (swSettings?.components?.Home?.resources?.Language as string) ?? "en-US";
};

export const DurationSelector = () => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [isSameDateSelected, setIsSameDateSelected] = useState(false);

    const { minDate, maxDate } = useMinMaxDate();
    const pageDuration = useDurationContext();
    const [duration, setDuration] = useState<IDuration>(pageDuration.raw);
    const [prevPageDurationRaw, setPrevPageDurationRaw] = useState(pageDuration.raw);
    const presets = usePresets();
    const selectedDurationPreset = useSelectedPreset({ duration, presets });
    const pageDurationPreset = useSelectedPreset({ duration: pageDuration.raw, presets });

    if (pageDuration.raw !== prevPageDurationRaw) {
        setPrevPageDurationRaw(pageDuration.raw);
        setDuration(pageDuration.raw);
    }

    const handlePresetSelect = (preset: IDropdownItem<IDuration>) => {
        if (preset.value) {
            setDuration(preset.value);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSubmit = () => {
        const durationString = formatDuration(duration);

        proNavigator.updateQueryParams({ duration: durationString, isDurationChanged: undefined }, { reload: false });
        handleClose();
    };

    const buttonText = useMemo<string>(() => {
        if (pageDurationPreset) {
            return pageDurationPreset.text;
        }

        if (pageDuration.raw.from.isSame(pageDuration.raw.to, "day")) {
            return pageDuration.raw.from.format(shortMonthDayAndYear);
        }

        return `${pageDuration.raw.from.format(shortMonthDayAndShortYear)} - ${pageDuration.raw.to.format(shortMonthDayAndShortYear)}`;
    }, [pageDuration, pageDurationPreset]);

    return (
        <Popover
            placement="bottom-end"
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <PopoverTrigger asChild>
                <DurationSelectorButton data-automation="duration-selector-control">
                    {buttonText}
                </DurationSelectorButton>
            </PopoverTrigger>
            <StyledPopoverContent>
                <DurationSelectorStateless
                    locale={getUserLanguage()}
                    presets={presets}
                    // @ts-ignore FIXME: strict migration - TS2322 'selectedPreset' typed as 'string' but is undefined when no preset matches — the library handles undefined at runtime
                    selectedPreset={selectedDurationPreset?.id}
                    presetsLabel=""
                    submitButtonText={translate("common.apply")}
                    customPresetText=""
                    minDate={minDate}
                    maxDate={maxDate}
                    minDailyDate={minDate}
                    maxDailyDate={maxDate}
                    durationChange={setDuration}
                    // @ts-ignore FIXME: strict migration - TS2322 library types presetSelect as (preset: IDropdownItem<unknown>) => void; presets are typed with IDuration values, so we narrow via the handler signature
                    presetSelect={handlePresetSelect}
                    selectorSubmit={handleSubmit}
                    closePopup={handleClose}
                    selectedGranularity={EGranularity.DAILY}
                    useDailyCalendar={true}
                    compareAllowed={false}
                    compareLabel=""
                    compareDisabledTooltipText=""
                    compareSelected={false}
                    compareEnabled={false}
                    compareTypesList={[]}
                    compareTypesSelected=""
                    compareDurationSelected={{ primary: duration, secondary: undefined }}
                    compareToggle={noop}
                    compareSelect={noop}
                    onSameDateClickedByUser={setIsSameDateSelected}
                    sameDateClickedByUser={isSameDateSelected || selectedDurationPreset?.id === SAME_DAY_PRESET_ID}
                />
            </StyledPopoverContent>
        </Popover>
    );
};
